


































import { Vue, Prop, Component, Watch, Ref } from 'vue-property-decorator'
import A from '@/utilities/alpha'
import {
  TemplateSubmissionPayload,
  NewTemplatePayload,
  randomId,
  getPlainTextLabel, defaultFieldValue
} from '@/utilities/template-helpers'
import { renderQuestions } from '@/utilities/template-renderer'
import generateQuestion from '@/utilities/template-question-generator'
import * as db from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import TemplateQuestions from './TemplateQuestions.vue'
import store from '@/store'
import analytics from '@/analytics'

type QuestionWithIndex = db.Question & { idx?: number }

interface idxObj {
  idx: number
  option: keyof db.Question
  value: string
}

export enum Location {
  AddTestModal = 'AddTestModal',
  DraftOrSubmittedTest = 'DraftOrSubmittedTest',
  DefaultDraftTestCreation = 'DefaultDraftTestCreation', // aka test creation 2.0 ... where tests are instantly created in draft state
  TemplateTestModal = 'TemplateTestModal'
}

@Component({
  components: {
    TemplateQuestions
  }
})
export default class TemplateManager extends Vue {
  @Prop({ required: true }) readonly title!: string
  @Prop({ required: true }) readonly initialTemplate!: { template: db.Template, templateValues: Record<string, db.TemplateValue> | null }
  @Prop({ default: () => ({}) }) readonly assets!: db.TestPayload['prototypes']
  @Prop({ default: false }) readonly editMode!: boolean
  @Prop({ required: true }) readonly location!: Location
  @Ref('templateQuestions') readonly templateQuestionsComponent!: TemplateQuestions

  async mounted () {
    await this.setTemplateFieldValues({ ...this.initialTemplate })

    if (this.location === Location.TemplateTestModal) {
      this.showErrorStyles = false
    }

    if (this.location === Location.DefaultDraftTestCreation) {
      this.showWarningStyles = true
      this.showErrorStyles = false
      await Vue.nextTick()
      this.setIsTemplateValid()
    }
  }

  alteredTemplate = false
  showWarningStyles = false
  showErrorStyles = true
  removedQuestions: db.Question[] = []
  addedCustomQuestions: db.Question[] = []
  templateQuestions: db.Question[] = []
  templateFields: Record<string, db.TemplateField> = {}
  templateFieldValues: Record<string, db.TemplateValue> = {}

  get acl () {
    if (this.location === Location.AddTestModal || this.location === Location.DefaultDraftTestCreation || this.location === Location.TemplateTestModal) {
      return store.state.testCreation.acl
    } else if (this.location === Location.DraftOrSubmittedTest) {
      return store.state.user.acl?.permissions
    }
  }

  get renderedTestTemplate () {
    const templateQuestions = A.clone(this.templateQuestions)
    return renderQuestions(templateQuestions, this.templateFields, this.templateFieldValues)
  }

  get templateSubmissionPayload (): TemplateSubmissionPayload {
    const payload: TemplateSubmissionPayload = {
      renderedTemplate: this.renderedTestTemplate,
      templateFieldValues: A.clone(this.templateFieldValues)
    }

    if (this.alteredTemplate) {
      // Account for a new template being created when a question was removed while adding
      payload.saveNewTemplate = this.alteredTemplate
      payload.newTemplatePayload = this.getNewTemplatePayload(`${this.title}_child`)
    } else {
      // Otherwise tell the server which template this is based on
      payload.testTemplateId = this.initialTemplate.template.id
    }
    return payload
  }

  @Watch('templateSubmissionPayload', { deep: true })
  templateSubmissionPayloadUpdated (newValue: TemplateSubmissionPayload) {
    this.$emit('templateSubmissionPayloadUpdated', newValue)
  }

  setValuesAltered (val: boolean) {
    this.$emit('valuesAltered', val)
  }

  @Watch('initialTemplate')
  async setTemplateFieldValues (newValues: { template: db.Template, templateValues: Record<string, db.TemplateValue> | undefined | null }) {
    this.templateQuestions = newValues.template.questions
    this.templateFields = newValues.template.template_fields

    if (newValues.templateValues) {
      this.templateFieldValues = newValues.templateValues
    } else {
      this.templateFieldValues = Object.keys(this.templateFields).reduce((acc: Record<string, db.TemplateValue>, key: string) => {
        const value = this.templateFields[key].value ?? defaultFieldValue(this.templateFields[key])
        acc[key] = { value }
        return acc
      }, {})
    }

    this.setValuesAltered(false)
    this.alteredTemplate = false
    this.removedQuestions = []
    this.addedCustomQuestions = []
  }

  setIsTemplateValid () {
    const isTemplateValid = this.validate()
    store.dispatch.testCreation.setTestValidForSubmission(isTemplateValid)
  }

  validate () {
    return this.templateQuestionsComponent.validate()
  }

  didSubmit (test: Pick<graph.Test, 'id'>, projectId?: string | null) {
    if (this.removedQuestions.length) {
      this.removedQuestions.forEach(question => {
        analytics.track('Question removed', {
          test,
          projectId,
          testType: 'Template',
          question
        })
      })
    }
    if (this.addedCustomQuestions.length) {
      this.addedCustomQuestions.forEach(question => {
        analytics.track('Custom Question Added', {
          test,
          projectId,
          testType: 'Template',
          question
        })
      })
    }
  }

  clearValidation () {
    this.templateQuestionsComponent.clearValidation()
  }

  getInputField (id: string) {
    return this.templateFieldValues[id]
  }

  getQuestionIdx (id: string) {
    const templateQuestions = A.clone(this.templateQuestions)
    return templateQuestions.find(question => question.id === id)
  }

  getNewTemplatePayload (templateTitle: string): NewTemplatePayload {
    const questions = A.clone(this.templateQuestions)
    const templateFields = A.clone(this.templateFields)
    return {
      title: templateTitle,
      questions: questions,
      templateFields: templateFields,
      parentId: this.initialTemplate.template.id
    }
  }

  updateInputValue ({ id, value, idx, nonTemplateQuestion, questionId }: { id: string, value: any, idx?: number, nonTemplateQuestion: boolean, questionId: string }) {
    // List update indexes within the value object of the field
    if (typeof idx === 'number') {
      const existingVals = A.clone(this.templateFieldValues[id].value)
      if (!(existingVals instanceof Array)) {
        console.error('Trying to splice a non-array')
        return
      }
      existingVals.splice(idx, 1, value)
      value = existingVals
    }
    if (nonTemplateQuestion) {
      const templateQuestions = A.clone(this.templateQuestions)
      const question = templateQuestions.find(question => question.id === questionId)
      if (!question || !('templateFields' in question)) return
      const input = question.templateFields.find(input => input.id === id)!
      const inputPlacement: string = input.placement!
      if (['min-label', 'max-label', 'min-rating', 'max-rating'].includes(inputPlacement!)) {
        const camelCasedPlacement = A.toCamel(inputPlacement) as 'minLabel' | 'maxLabel' | 'minRating' | 'maxRating'
        // @ts-ignore
        question.scale.properties[camelCasedPlacement] = value
        if (inputPlacement === 'min-label') question.scale!.grouping![2].label = value
        else if (inputPlacement === 'max-label') question.scale!.grouping![0].label = value
      } else if (id.includes('list') && question.answers) {
        question.answers = value.map((answer: string) => ({ label: answer }))
      }
      this.templateQuestions = templateQuestions
    }

    // Doing this registers the new values with Vue so that computed properties trigger auto update
    const newTemplateFieldValue = Object.assign({}, this.templateFieldValues[id], { value })
    this.$set(this.templateFieldValues, id, newTemplateFieldValue)
    this.setValuesAltered(true)
  }

  updateLockValue ({ id, lockValue }: { id: string, lockValue: boolean }) {
    this.templateFieldValues[id].lockOptions = lockValue
    this.setValuesAltered(true)
  }

  updateVariantAsset ({ id, asset }: { id: string, asset: any }) {
    this.templateFieldValues[id].asset = asset
    this.setValuesAltered(true)
  }

  deleteVariant (variantId: string) {
    const templateFieldValues = A.clone(this.templateFieldValues)
    delete templateFieldValues[variantId]
    if (!Object.keys(templateFieldValues).filter(key => key.split('-').includes('variant')).length) {
      // Turn the initial fields back to original form
      Object.keys(templateFieldValues).forEach(key => {
        if (templateFieldValues[key].variant) {
          delete templateFieldValues[key].variant
          delete templateFieldValues[key].variantLabel
        }
      })
    }
    this.templateFieldValues = templateFieldValues
  }

  setVariantLabel ({ variantId, value }: { variantId: string, value: string }) {
    const templateFieldValues = A.clone(this.templateFieldValues)
    templateFieldValues[variantId].variantLabel = value
    this.templateFieldValues = templateFieldValues
  }

  deleteInputValue ({ id, idx, questionId }: { id: string, idx: number, questionId?: string }) {
    const value = this.templateFieldValues[id].value
    if (!(value instanceof Array)) {
      console.error('Trying to splice a non-array')
      return
    }
    const inputs = value.slice()
    inputs.splice(idx, 1)
    if (questionId) {
      const templateQuestions = A.clone(this.templateQuestions)
      const question = templateQuestions.find(question => question.id === questionId)!
      question.answers && question.answers.splice(idx, 1)
      this.templateQuestions = templateQuestions
    }
    this.templateFieldValues[id].value = inputs
  }

  deleteQuestion (id: string) {
    const templateQuestions = A.clone(this.templateQuestions)
    const removedQuestions = A.clone(this.removedQuestions)
    const templateFields = A.clone(this.templateFields)
    const templateFieldValues = A.clone(this.templateFieldValues)
    const questionToRemoveIndex = templateQuestions.findIndex(question => question.id === id)
    if (questionToRemoveIndex === -1) {
      console.error('Question not found with id' + id)
      return
    }
    const questionToRemove: QuestionWithIndex = templateQuestions[questionToRemoveIndex]
    questionToRemove.idx = questionToRemoveIndex

    if (!questionToRemove.custom) {
      removedQuestions.push(questionToRemove)
    } else {
      // Remove this custom question from the custom question tracking
      const remainingCustomQuestions = this.addedCustomQuestions.filter(customQuestion => {
        return customQuestion.id !== questionToRemove.id
      })
      this.addedCustomQuestions = remainingCustomQuestions
    }
    templateQuestions.splice(questionToRemove.idx!, 1)

    // Check remaining questions and determine which templateFieldValues should remain
    const remainingFields = templateQuestions.reduce((acc: string[], question: db.Question) => {
      if ('templateFields' in question) {
        question.templateFields.forEach((field: db.TemplateField) => {
          // Add field id to the array
          if (!field.id) return
          acc.push(field.id)
        })
      }
      return acc
    }, [])

    Object.keys(templateFields).forEach(key => {
      if (!remainingFields.includes(key)) {
        delete templateFields[key]
        delete templateFieldValues[key]
      }
    })
    this.templateQuestions = templateQuestions
    this.templateFields = templateFields
    this.templateFieldValues = templateFieldValues
    this.alteredTemplate = true
    this.removedQuestions = removedQuestions
  }

  editQuestion (id: string) {
    const templateQuestions = A.clone(this.templateQuestions)
    const templateFields = A.clone(this.templateFields)
    const templateFieldValues = A.clone(this.templateFieldValues)
    const questionToEditIndex = templateQuestions.findIndex(question => question.id === id)
    if (questionToEditIndex === -1) return console.error('Question not found with id' + id)

    const questionToEdit: db.Question = templateQuestions[questionToEditIndex]

    // Construct a plain-text version of the questionPrompt
    const questionPrompt = getPlainTextLabel(questionToEdit, templateFields, templateFieldValues)

    // Depending on the question type, assign options for generateQuestion.
    const { questionType, answers, scale } = questionToEdit
    let listOptions, listId
    if (scale) var { properties: { minLabel, maxLabel, minRating, maxRating } } = scale
    if (['single-select', 'multi-select', 'rank'].includes(questionType)) {
      if (questionToEdit.answers) {
        listOptions = questionToEdit.answers.map(answer => answer.label)
      } else if ('templateFields' in questionToEdit) {
        listId = questionToEdit.templateFields?.find(templateField => templateField.id!.includes('list'))!.id as string
        listOptions = templateFieldValues[listId].value
      }
    } else if (questionType === 'matrix-likert' && questionToEdit.answers) {
      listOptions = answers!.map(answer => answer.label)
    }

    // Create options to pass into generateQuestion
    const allQuestionOptions: Record<db.Question['questionType'] | string, any> = {
      textarea: {},
      description: {},
      'single-select': {
        listOptions,
        listId,
        answers: questionToEdit.answers
      },
      'multi-select': {
        listOptions,
        listId
      },
      rate: {
        minLabel,
        maxLabel,
        minRating,
        maxRating,
        scale
      },
      'rate-only': {
        minLabel,
        maxLabel,
        minRating,
        maxRating,
        scale
      },
      rank: {
        listId,
        listOptions
      },
      'matrix-likert': {
        answers,
        listOptions,
        scale,
        minLabel,
        maxLabel,
        minRating,
        maxRating
      }
    }
    const options = Object.assign(allQuestionOptions[questionType], {
      custom: false,
      edited: true,
      explain: !!questionToEdit.explain,
      question: questionPrompt,
      questionId: questionToEdit.id
    })

    // Generate new question data. newFields and newValues will already be in newQuestion.
    const { newFields, newValues, newQuestion } = generateQuestion(questionType, options)

    // Merge in new data locally
    const prependedField = 'templateFields' in questionToEdit && questionToEdit.templateFields.find(field => field.placement === 'prepend')
    prependedField && newQuestion.templateFields.push(prependedField)
    Object.assign(templateFieldValues, newValues)
    Object.assign(templateFields, newFields)
    templateQuestions.splice(questionToEditIndex, 1, newQuestion)

    // Merge new data into component state
    this.templateQuestions = templateQuestions
    this.templateFields = templateFields
    this.templateFieldValues = templateFieldValues
    this.alteredTemplate = true
  }

  addQuestion (type: db.Question['questionType']) {
    const { newFields, newValues, newQuestion } = generateQuestion(type, { custom: true, edited: false })
    const addedCustomQuestions = A.clone(this.addedCustomQuestions)
    const templateQuestions = A.clone(this.templateQuestions)
    templateQuestions.push(newQuestion)
    addedCustomQuestions.push(newQuestion)
    const templateFields = Object.assign({}, this.templateFields, newFields)
    const templateFieldValues = Object.assign({}, this.templateFieldValues, newValues)

    this.templateQuestions = templateQuestions
    this.templateFields = templateFields
    this.templateFieldValues = templateFieldValues
    this.alteredTemplate = true
    this.addedCustomQuestions = addedCustomQuestions
  }

  addSplitTestOption (question: db.QuestionWithFields) {
    let variantIncludesAsset = false
    let currentVariant = 0
    const variantInitialNameMap: Record<number, string> = {
      0: 'Variant B',
      1: 'Variant C',
      2: 'Variant D'
    }
    // Turn the current field values into variants and create new variants
    question.templateFields.forEach(field => {
      if (field.id) {
        const key: string = field.id || ''
        if (!this.templateFieldValues[key].variant) {
          this.templateFieldValues[key].variant = true
          this.templateFieldValues[key].variantLabel = 'Variant A'
        }
        if (key.split('-').includes('asset')) variantIncludesAsset = true
      }
    })

    const variantsPresent = Object.keys(this.templateFieldValues).filter(key => key.split('-').includes('variant')).length
    if (variantsPresent) currentVariant += variantsPresent
    const newVariant = {} as Record<string, db.TemplateValue>
    const key = `${randomId()}-variant`
    newVariant[key] = {
      value: '',
      variant: true,
      variantLabel: variantInitialNameMap[currentVariant]
    }
    if (variantIncludesAsset) {
      newVariant[key].asset = ''
    }
    this.templateFieldValues = Object.assign({}, this.templateFieldValues, newVariant)
  }

  answersChange (payload: { questionId: string, answers: { label: string }[] }) {
    const { questionId, answers } = payload
    const tqClone = A.clone(this.templateQuestions)
    const question = tqClone.find(({ id }) => id === questionId)
    if (!question) {
      console.error(`Unable to find question by id ${questionId}`)
      return
    }
    question.answers = answers
    this.templateQuestions = tqClone
  }

  scaleChange (payload: { questionId: string, newTFV: Record<string, { value: string | number, placement: string }> }) {
    const { questionId, newTFV } = payload
    const tqClone = A.clone(this.templateQuestions)
    const question = tqClone.find(({ id }) => id === questionId)
    if (!question) {
      console.error(`Unable to find question by id ${questionId}`)
      return
    }
    if (!question.scale) return
    let minLabel, maxLabel, minRating, maxRating
    for (const id in newTFV) {
      const { placement } = newTFV[id]
      if (placement === 'min-rating') minRating = newTFV[id].value as number
      else if (placement === 'max-rating') maxRating = newTFV[id].value as number
      else if (placement === 'min-label') minLabel = newTFV[id].value as string
      else if (placement === 'max-label') maxLabel = newTFV[id].value as string
    }
    Object.assign(question.scale.properties, {
      minLabel,
      maxLabel,
      minRating,
      maxRating
    })
    if (question.scale.grouping && maxLabel && minLabel) {
      question.scale.grouping[0].label = maxLabel
      question.scale.grouping[2].label = minLabel
    }
    this.templateQuestions = tqClone
  }

  reorderQuestion (reorderOptions: { idx: number, direction: number }) {
    const { idx, direction } = reorderOptions
    const templateQuestions = A.clone(this.templateQuestions)
    const questionToMove = templateQuestions[idx]
    // Remove question from current position
    templateQuestions.splice(idx, 1)
    // Place in new postion
    templateQuestions.splice(idx + direction, 0, questionToMove)

    this.templateQuestions = templateQuestions
    this.alteredTemplate = true
  }

  setQuestionOption ({ idx, option, value }: idxObj) {
    const templateQuestions = A.clone(this.templateQuestions)
    const question: any = A.clone(this.templateQuestions[idx])
    question[option] = value
    templateQuestions[idx] = question
    this.templateQuestions = templateQuestions
    this.setValuesAltered(true)
  }

  setInputValue (id: string, value: any) {
    const newPayload = A.clone(this.templateSubmissionPayload)
    if (!newPayload.templateFieldValues) {
      console.error('Missing template field values')
      return
    }
    if (!newPayload.templateFieldValues[id]) {
      newPayload.templateFieldValues[id] = { value: undefined }
    }
    newPayload.templateFieldValues![id].value = value
    this.$emit('change', newPayload)
  }
}
